import React, { useState, useEffect } from "react"
import { Container, Card, CardBody, Row, Col, Button, Input, Table } from "reactstrap"
import Breadcrumb from "components/Common/Breadcrumb"
import {
  getCorretoras,
  createCorretora,
  updateCorretora,
  deleteCorretora,
} from "helpers/corretora_helper"
import { FaEdit, FaTrash, FaPlus } from "react-icons/fa"
import toastr from "toastr"
import "toastr/build/toastr.min.css"

const Corretoras = () => {
  document.title = "Corretoras | Blops Leads"

  const [corretoras, setCorretoras] = useState([])
  const [nome, setNome] = useState("")
  const [editando, setEditando] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    carregarCorretoras()
  }, [])

  const carregarCorretoras = async () => {
    setLoading(true)
    try {
      const response = await getCorretoras()
      // Filtrar apenas as ativas (não excluídas)
      const corretorasAtivas = response.filter(c => !c.DataExclusao)
      setCorretoras(corretorasAtivas)
    } catch (error) {
      console.error("Erro ao carregar corretoras:", error)
      toastr.error("Erro ao carregar corretoras", "Erro")
    } finally {
      setLoading(false)
    }
  }

  const handleSalvar = async () => {
    if (!nome.trim()) {
      toastr.warning("Por favor, informe o nome da corretora", "Atenção")
      return
    }

    setLoading(true)
    try {
      if (editando) {
        await updateCorretora(editando.Id, nome)
        toastr.success("Corretora atualizada com sucesso!", "Sucesso")
      } else {
        await createCorretora(nome)
        toastr.success("Corretora criada com sucesso!", "Sucesso")
      }
      setNome("")
      setEditando(null)
      carregarCorretoras()
    } catch (error) {
      console.error("Erro ao salvar corretora:", error)
      if (error.response?.status === 400) {
        toastr.error(error.response.data.message || "Dados inválidos", "Erro")
      } else {
        toastr.error("Erro ao salvar corretora", "Erro")
      }
    } finally {
      setLoading(false)
    }
  }

  const handleEditar = corretora => {
    setNome(corretora.Nome)
    setEditando(corretora)
    // Scroll para o topo do formulário
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  const handleCancelar = () => {
    setNome("")
    setEditando(null)
  }

  const handleExcluir = async id => {
    if (window.confirm("Deseja realmente excluir esta corretora?")) {
      setLoading(true)
      try {
        await deleteCorretora(id)
        toastr.success("Corretora excluída com sucesso!", "Sucesso")
        carregarCorretoras()
      } catch (error) {
        console.error("Erro ao excluir corretora:", error)
        if (error.response?.status === 400) {
          toastr.error(
            error.response.data.message || "Não foi possível excluir",
            "Erro"
          )
        } else {
          toastr.error("Erro ao excluir corretora", "Erro")
        }
      } finally {
        setLoading(false)
      }
    }
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb
            title="Cadastros"
            breadcrumbItem="Corretoras"
          />

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <h4 className="card-title mb-4">
                    {editando ? "Editar Corretora" : "Nova Corretora"}
                  </h4>

                  {editando && (
                    <div className="alert alert-info" role="alert">
                      <i className="mdi mdi-alert-circle-outline me-2"></i>
                      Você está editando a corretora: <strong>{editando.Nome}</strong>
                    </div>
                  )}

                  <Row className="mb-3">
                    <Col md={8}>
                      <Input
                        type="text"
                        className="form-control"
                        placeholder="Nome da Corretora"
                        value={nome}
                        onChange={e => setNome(e.target.value)}
                        disabled={loading}
                        onKeyPress={e => {
                          if (e.key === "Enter") {
                            handleSalvar()
                          }
                        }}
                      />
                    </Col>
                    <Col md={4}>
                      <Button
                        color="primary"
                        onClick={handleSalvar}
                        disabled={loading}
                        className="me-2"
                      >
                        <FaPlus className="me-1" />
                        {editando ? "Atualizar" : "Criar"}
                      </Button>
                      {editando && (
                        <Button
                          color="secondary"
                          onClick={handleCancelar}
                          disabled={loading}
                        >
                          Cancelar
                        </Button>
                      )}
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <h4 className="card-title mb-4">Lista de Corretoras</h4>

                  {loading ? (
                    <div className="text-center">
                      <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Carregando...</span>
                      </div>
                    </div>
                  ) : corretoras.length === 0 ? (
                    <div className="text-center text-muted">
                      <p>Nenhuma corretora cadastrada</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table className="table table-striped table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: "80px" }}>ID</th>
                            <th>Nome</th>
                            <th style={{ width: "150px" }} className="text-center">
                              Ações
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {corretoras.map(c => (
                            <tr key={c.Id}>
                              <td>{c.Id}</td>
                              <td>{c.Nome}</td>
                              <td className="text-center">
                                <Button
                                  color="info"
                                  size="sm"
                                  className="me-2"
                                  onClick={() => handleEditar(c)}
                                  title="Editar"
                                >
                                  <FaEdit />
                                </Button>
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => handleExcluir(c.Id)}
                                  title="Excluir"
                                >
                                  <FaTrash />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default Corretoras

